import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { styled } from 'baseui';
import { PLACEMENT, Popover } from 'baseui/popover';
import type { ShowNotificationT } from '../../../../../../common/types/types';
import type { BoundingBoxTrackingInputT, BoundingBoxTrackingOutputT, SAMSegmentVideoData } from '../../../../../types';
import { getToolIcon } from '../../../toolbar/utils';
import { AnnotationTimelineConfigLabelStore } from '../annotation-timeline-config-label-store';
import { POPOVER_OVERRIDES } from '../modernised-annotation-timeline-modal/popover-styles';
import { getActionConfig } from '../utils';
import { useAnnotationTimelineHandlers } from './hooks/use-annotation-timeline-handlers';
import { EDIT_ANNOTATION_CELL_Z_INDEX } from './constants';
import type { AnnotationDataT, EditAnnotationPanelRefT } from './types';
export type AnnotationConfigModalPropsT = {
  showNotification?: ShowNotificationT;
  fetchAnnotationTrackingData?: (labelTitle: string, frameRange?: {
    start: number;
    end: number;
  }) => Promise<SAMSegmentVideoData | Error>;
  getBoundingBoxTrackingData?: (frameIndex: number, bbox: BoundingBoxTrackingInputT['bbox'], numFramesToTrack: number) => Promise<BoundingBoxTrackingOutputT | Error>;
};
type PropsT = AnnotationConfigModalPropsT & {};
type PopoverContentPropsT = AnnotationConfigModalPropsT & {
  annotationData: AnnotationDataT;
  onClose: () => void;
};
const AnchorDiv = styled<'div', {
  $anchorNode: HTMLDivElement | null;
}>('div', ({
  $anchorNode
}) => {
  if (!$anchorNode) {
    return {
      display: 'none'
    };
  }
  const boundingRect = $anchorNode.getBoundingClientRect();
  return {
    position: 'fixed',
    top: `${boundingRect.top}px`,
    left: `${boundingRect.left}px`,
    width: `${boundingRect.width}px`,
    height: `${boundingRect.height}px`,
    pointerEvents: 'none',
    zIndex: EDIT_ANNOTATION_CELL_Z_INDEX
  };
});
const PopoverContent = ({
  annotationData,
  onClose,
  showNotification,
  getBoundingBoxTrackingData,
  fetchAnnotationTrackingData
}: PopoverContentPropsT) => {
  const {
    annotationId,
    annotationInfo
  } = annotationData;
  const annotationLabel = annotationInfo.annotationLabel;
  const Icon = getToolIcon(annotationInfo.annotationType);
  const isBoundingBoxTrackingAvailable = Boolean(getBoundingBoxTrackingData);
  const actionConfig = getActionConfig(annotationInfo.annotationType, isBoundingBoxTrackingAvailable);
  const {
    handleOnChange
  } = useAnnotationTimelineHandlers({
    showNotification,
    getBoundingBoxTrackingData,
    fetchAnnotationTrackingData
  });
  return <AnnotationTimelineConfigLabelStore annotationId={annotationId} annotationLabel={annotationLabel} Icon={Icon} actionConfig={actionConfig} onClose={onClose} onChange={async (newRanges, isAnnotationTracking) => {
    await handleOnChange(annotationId, annotationInfo.annotationType, newRanges, onClose, isAnnotationTracking);
  }} />;
};
export const EditAnnotationPanel = forwardRef<EditAnnotationPanelRefT, PropsT>((props, ref) => {
  const {
    ...annotationConfigModalProps
  } = props;
  const [anchorNode, setAnchorNode] = useState<HTMLDivElement | null>(null);
  const [annotationData, setAnnotationData] = useState<AnnotationDataT | null>(null);
  const handleClose = useCallback(() => {
    setAnchorNode(null);
    setAnnotationData(null);
  }, []);
  useImperativeHandle(ref, () => ({
    open: (anchorNode: HTMLDivElement, annotationData: AnnotationDataT) => {
      setAnchorNode(anchorNode);
      setAnnotationData(annotationData);
    },
    close: handleClose
  }), [handleClose]);
  const overrides = useMemo(() => {
    return {
      ...POPOVER_OVERRIDES,
      Body: {
        ...POPOVER_OVERRIDES.Body,
        props: {
          onClick: e => e.stopPropagation(),
          onMouseMove: e => e.stopPropagation(),
          onMouseDown: e => e.stopPropagation(),
          onMouseUp: e => e.stopPropagation()
        }
      }
    };
  }, []);
  return <Popover isOpen={!!anchorNode} onClickOutside={handleClose} onEsc={handleClose} content={annotationData ? <PopoverContent annotationData={annotationData} onClose={handleClose} {...annotationConfigModalProps} /> : null} showArrow placement={PLACEMENT.right} autoFocus={false} overrides={overrides}>
      <AnchorDiv $anchorNode={anchorNode} />
    </Popover>;
});
EditAnnotationPanel.displayName = 'EditAnnotationPanel';